import { type UseQueryResult, useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { ContextSources } from '@air/api';
import { type ApiResponse, callAirApi } from '@air/utils-api';

import { type MentionItem } from '../../ui/components/ChatComposer/mentions/types';
import { getPrivateContextSourcesGetKey } from './queryKeys';
import { usePrivateContextSourcesList } from './usePrivateContextSourcesList';

type ContextSourceGetResult = ApiResponse<typeof ContextSources.get>;

type BrandKit = Pick<ContextSourceGetResult, 'id' | 'name'> & {
  items: MentionItem[];
};

const mapFieldValueToMentionItem = (
  source: ContextSourceGetResult,
  field: NonNullable<ContextSourceGetResult['fields']>[number],
  value: NonNullable<NonNullable<ContextSourceGetResult['fields']>[number]['values']>[number],
): MentionItem | null => {
  const name = value.label?.trim() || field.name;

  if (field.valueType === 'color' && typeof value.value === 'string') {
    return {
      brandKitId: source.id,
      hex: value.value,
      id: value.id,
      name,
      thumbnailUrl: null,
      type: 'color',
    };
  }

  if (field.valueType === 'font' && typeof value.value === 'object' && value.value && 'family' in value.value) {
    return {
      brandKitId: source.id,
      fontFamily: value.value.family,
      id: value.id,
      name: value.label?.trim() || value.value.family,
      thumbnailUrl: 'media' in value.value ? (value.value.media?.original ?? null) : null,
      type: 'font',
    };
  }

  if (field.valueType === 'file' && typeof value.value === 'object' && value.value && 'media' in value.value) {
    const url = value.value.media?.image ?? value.value.media?.original;

    if (!url) {
      return null;
    }

    return {
      brandKitId: source.id,
      id: value.id,
      name,
      thumbnailUrl: url,
      type: 'logo',
    };
  }

  return null;
};

const mapContextSourceToBrandKit = (source: ContextSourceGetResult): BrandKit => {
  const items: MentionItem[] = [];

  for (const field of source.fields ?? []) {
    for (const value of field.values ?? []) {
      const item = mapFieldValueToMentionItem(source, field, value);

      if (item) {
        items.push(item);
      }
    }
  }

  return {
    id: source.id,
    items,
    name: source.name,
  };
};

const combineBrandKits = (results: UseQueryResult<ContextSourceGetResult>[]): BrandKit[] => {
  const kits: BrandKit[] = [];

  for (const result of results) {
    if (result.data) {
      kits.push(mapContextSourceToBrandKit(result.data));
    }
  }

  return kits;
};

const combineBrandKitQueries = (
  results: UseQueryResult<ContextSourceGetResult>[],
  listFetched: boolean,
  expectedCount: number,
) => ({
  brandKits: combineBrandKits(results),
  isBrandKitsReady: listFetched && (expectedCount === 0 || results.every((result) => result.isFetched)),
});

export const useBrandKitMentions = (workspaceId: string) => {
  const { data: listData, isFetched: isListFetched } = usePrivateContextSourcesList({ workspaceId });
  const sources = listData?.data ?? [];

  const { brandKits, isBrandKitsReady } = useQueries({
    combine: (results) => combineBrandKitQueries(results, isListFetched, sources.length),
    queries: sources.map((source) => {
      const params = { contextSourceId: source.id, workspaceId };

      return {
        queryFn: () => callAirApi(() => ContextSources.get(params)),
        queryKey: getPrivateContextSourcesGetKey(params),
        refetchOnWindowFocus: true,
      };
    }),
  });

  const brandKitMentions = useMemo(() => brandKits.flatMap((kit) => kit.items), [brandKits]);

  return { brandKitMentions, brandKits, isBrandKitsReady };
};
